import React from 'react';
import { Modal, Empty, Spin } from 'antd';
import { MdRequestPage } from 'react-icons/md';
import { IStoryRequestInfo } from '@coreStory/type';
import styles from './index.module.scss';

interface StoryRequestModalProps {
  visible: boolean;
  onCancel: () => void;
  storyRequestInfo: IStoryRequestInfo[] | null;
  isLoading: boolean;
  error: any;
}

const StoryRequestModal: React.FC<StoryRequestModalProps> = ({
  visible,
  onCancel,
  storyRequestInfo,
  isLoading,
  error,
}) => {
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <Spin size="large" />
          <p>Loading story request information...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.errorContainer}>
          <Empty
            image={<MdRequestPage size={48} className={styles.errorIcon} />}
            description={
              <div className={styles.errorDescription}>
                <h3>Error Loading Story Request</h3>
                <p>
                  Failed to load story request information. Please try again.
                </p>
              </div>
            }
          />
        </div>
      );
    }

    if (!storyRequestInfo || storyRequestInfo.length === 0) {
      return (
        <div className={styles.emptyContainer}>
          <Empty
            image={<MdRequestPage size={48} className={styles.emptyIcon} />}
            description={
              <div className={styles.emptyDescription}>
                <h3>No Story Request Available</h3>
                <p>
                  This story doesn&apos;t have any request information
                  available.
                </p>
              </div>
            }
          />
        </div>
      );
    }

    const requestInfo = storyRequestInfo[0];

    return (
      <div className={styles.requestContent}>
        <div className={styles.requestHeader}>
          <div className={styles.iconContainer}>
            <MdRequestPage size={24} className={styles.requestIcon} />
          </div>
          <h3>Story Request Information</h3>
        </div>

        <div className={styles.requestBody}>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <label>Story Name:</label>
              <span>{requestInfo.StoryName}</span>
            </div>

            <div className={styles.infoItem}>
              <label>Child Name:</label>
              <span>{requestInfo.ChildName}</span>
            </div>

            <div className={styles.infoItem}>
              <label>Child Age:</label>
              <span>{requestInfo.ChildAge} years old</span>
            </div>

            <div className={styles.infoItem}>
              <label>Language:</label>
              <span>{requestInfo.Lang}</span>
            </div>

            <div className={styles.infoItem}>
              <label>Voice Speaker:</label>
              <span>{requestInfo.VoiceSpeaker}</span>
            </div>

            <div className={styles.infoItem}>
              <label>Voice Vibe:</label>
              <span>{requestInfo.VoiceVibe}</span>
            </div>

            <div className={styles.infoItem}>
              <label>Story Tone:</label>
              <span>{requestInfo.StoryTone}</span>
            </div>

            <div className={styles.infoItem}>
              <label>Point of View:</label>
              <span>{requestInfo.PointOfView}</span>
            </div>

            <div className={styles.infoItem}>
              <label>Story Style:</label>
              <span>{requestInfo.StoryStyle}</span>
            </div>

            <div className={styles.infoItem}>
              <label>Style Title:</label>
              <span>{requestInfo.StyleTitle}</span>
            </div>

            <div className={styles.infoItem}>
              <label>Character Title:</label>
              <span>{requestInfo.CharacterTitle}</span>
            </div>

            <div className={styles.infoItem}>
              <label>Training Topic:</label>
              <span>{requestInfo.TrainingTopicTitle}</span>
            </div>
          </div>

          <div className={styles.descriptionSection}>
            <label>Request Description:</label>
            <div className={styles.descriptionText}>
              {requestInfo.RequestDescription.split('\n').map(
                (paragraph, index) => (
                  <p key={index} className={styles.paragraph}>
                    {paragraph.trim()}
                  </p>
                ),
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      className={styles.storyRequestModal}
      centered>
      {renderContent()}
    </Modal>
  );
};

export default StoryRequestModal;
